import { DialogTitleWithButton } from '@core/components/ImageViewer/DialogTitleWithButton';
import { ImageContent } from '@core/components/ImageViewer/ImageContent';
import { InteractiveImageContainer } from '@core/components/ImageViewer/InteractiveImageContainer';
import { Dialog } from '@soteria/design-system-ve';
import { useEffect, useState } from 'react';

export interface ImageViewerProps {
  /**
   * Whether the image viewer is open
   */
  isOpen: boolean;

  /**
   * Function to close the image viewer
   */
  onClose: () => void;

  /**
   * URL of the image to display
   */
  imageUrl: string;

  /**
   * Optional alt text for the image
   */
  altText?: string;

  /**
   * Optional title to display above the image
   */
  title?: string;

  /**
   * Whether the content is safe (if false, enables blur toggle)
   */
  isSafe?: boolean;

  /**
   * Whether the content is blurred (controlled by parent)
   */
  isBlurred?: boolean;

  /**
   * Callback when blur state should be toggled
   */
  onToggleBlur?: () => void;
}

/**
 * ImageViewer Component
 *
 * A reusable fullscreen image viewer component that displays images in a modal dialog.
 * Handles image loading states and errors gracefully.
 * Supports blur toggle for unsafe content.
 *
 * Note: Image URLs should be complete URLs. This component does not handle
 * media service authentication - use direct image URLs only.
 *
 * @example
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 *
 * <ImageViewer
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   imageUrl="https://example.com/image.jpg"
 *   altText="Description of image"
 *   title="Image Title"
 *   isSafe={false}
 *   initialBlurred={true}
 * />
 * ```
 */
export function ImageViewer({ isOpen, onClose, imageUrl, altText = 'Image', title, isSafe = true, isBlurred = false, onToggleBlur }: ImageViewerProps) {
  const [imageLoadError, setImageLoadError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const canInteract = !isSafe && !imageLoading && !imageLoadError && Boolean(onToggleBlur);

  // Reset states when dialog opens or when imageUrl changes
  useEffect(() => {
    if (isOpen) {
      setImageLoadError(false);
      setImageLoading(true);
    }
  }, [isOpen, imageUrl]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageLoadError(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageLoadError(true);
  };

  const handleToggleBlur = () => {
    onToggleBlur?.();
  };

  const dialogTitle = isSafe ? title : <DialogTitleWithButton isBlurred={isBlurred} title={title} onToggleBlur={handleToggleBlur} />;

  return (
    <Dialog
      showCloseButton
      isOpen={isOpen}
      size="xxxl"
      // @ts-expect-error - XDS Dialog runtime accepts ReactNode for title, but types currently only allow string
      title={dialogTitle}
      onClose={onClose}
    >
      <InteractiveImageContainer canInteract={canInteract} isBlurred={isBlurred} onToggleBlur={onToggleBlur}>
        <ImageContent
          altText={altText}
          imageLoadError={imageLoadError}
          imageLoading={imageLoading}
          imageUrl={imageUrl}
          isBlurred={isBlurred}
          isSafe={isSafe}
          onImageError={handleImageError}
          onImageLoad={handleImageLoad}
        />
      </InteractiveImageContainer>
    </Dialog>
  );
}
